// Inspired by: https://github.com/excid3/tailwindcss-stimulus-components/blob/master/src/popover.js

import { Controller } from "@hotwired/stimulus";
import { createPopper } from "@popperjs/core";

// Gap between the popover and its trigger (the `offset` modifier) plus a little breathing room from
// the viewport edge — subtracted when working out how tall a dropdown list may be.
const TRIGGER_OFFSET = 8;
const VIEWPORT_PADDING = 8;
// Never shrink a scrollable list below this — a tiny scroll area is still usable.
const MIN_SCROLLER_HEIGHT = 96;
// Above the dialog component (z-[1010]) and its backdrop (z-[1009]).
const ABOVE_DIALOG_Z_INDEX = "1020";

export default class UIPopover extends Controller {
  static values = {
    dismissAfter: Number,
    matchWidth: { type: Boolean, default: false },
  };
  static targets = ["content", "wrapper", "trigger"];

  connect() {
    this._content = this.contentTarget;
    this.clickOutsideListener = this.clickOutside.bind(this);
    this.dismissOnScrollListener = this.dismissOnScroll.bind(this);
    this.element.addEventListener(
      "popover:update",
      this.updatePosition.bind(this),
    );
    // Closing a dialog only hides it, so an open dropdown would otherwise stay visible over the page.
    this._enclosingDialog = this.element.closest(
      "[data-controller~='ui--dialog']",
    );
    if (this._enclosingDialog) {
      this.dialogClosedListener = this.hide.bind(this);
      this._enclosingDialog.addEventListener(
        "ui--dialog:closed",
        this.dialogClosedListener,
      );
    }
  }

  // Lazily create the Popper instance on first use. When the controller
  // connects inside a hidden container (e.g. an iframe or collapsed panel),
  // Popper can't measure positions correctly, so we defer creation until the
  // popover is actually opened.
  ensurePopper() {
    if (this.popperInstance) return;

    const modifiers = [
      { name: "offset", options: { offset: [0, TRIGGER_OFFSET] } },
      { name: "preventOverflow", options: { padding: VIEWPORT_PADDING } },
    ];

    // Side menus (row/folder actions) choose their vertical direction themselves
    // in placementForOpen — don't let flip override it.
    const side = this._content.dataset.side || "bottom";
    if (side === "left" || side === "right") {
      modifiers.push({ name: "flip", enabled: false });
    }

    if (this.matchWidthValue) {
      modifiers.push({
        name: "sameWidth",
        enabled: true,
        phase: "beforeWrite",
        requires: ["computeStyles"],
        fn: ({ state }) => {
          state.styles.popper.width = `${state.rects.reference.width}px`;
        },
        effect: ({ state }) => {
          state.elements.popper.style.width = `${state.elements.reference.getBoundingClientRect().width}px`;
        },
      });
    }

    this.popperInstance = createPopper(this.triggerTarget, this._content, {
      placement: this.placementForOpen(),
      strategy: "fixed",
      modifiers,
    });
  }

  // A searchable dropdown (select/multiselect) marks its scrollable option list with
  // `data-popover-scroll`. Embedded forms render inside an iframe that iframe-resizer sizes to the
  // content height, so a dropdown taller than the room around its trigger would spill past the
  // frame's viewport and get clipped. Before Popper positions the popover, cap that list to the
  // room available so the whole dropdown stays on screen. We prefer opening downward (the
  // conventional direction) and just shrink the list to fit below; we only size to the space above
  // — which lets Popper's `flip` carry it upward — when there isn't enough room below for even a
  // minimal list and there's more room above. Sizing up front means Popper places an
  // already-correctly-sized popover, so it isn't repositioned or re-clipped afterwards.
  sizeScrollerToViewport() {
    const scroller = this._content.querySelector("[data-popover-scroll]");
    if (!scroller) return;

    // Reset first so we measure the list's natural CSS ceiling, not a previous open's cap.
    scroller.style.maxHeight = "";
    const ceiling =
      parseFloat(getComputedStyle(scroller).maxHeight) || Infinity;
    // Non-scroll chrome inside the popover (search bar, padding, borders).
    const chrome = this._content.offsetHeight - scroller.offsetHeight;

    const trigger = this.triggerTarget.getBoundingClientRect();
    const margin = TRIGGER_OFFSET + VIEWPORT_PADDING;
    const roomBelow = window.innerHeight - trigger.bottom - margin;
    const roomAbove = trigger.top - margin;
    const flipsUp =
      roomBelow - chrome < MIN_SCROLLER_HEIGHT && roomAbove > roomBelow;
    const room = flipsUp ? roomAbove : roomBelow;

    const target = Math.max(
      MIN_SCROLLER_HEIGHT,
      Math.min(ceiling, room - chrome),
    );
    scroller.style.maxHeight = `${Math.floor(target)}px`;
  }

  // Choose the placement when opening. A side menu (left/right) inside a scroll
  // container expands toward the roomier half so it stays inside the container:
  // down (`-start`) when its trigger is in the top half of the visible scroll
  // area, up (`-end`) when in the bottom half — measured live, so the current
  // scroll position is accounted for. Other popovers use their configured align.
  placementForOpen() {
    const side = this._content.dataset.side || "bottom";
    if (side === "left" || side === "right") {
      const scroller = this.scrollableAncestors()[0];
      if (scroller) {
        const trigger = this.triggerTarget.getBoundingClientRect();
        const box = scroller.getBoundingClientRect();
        const expandDown =
          trigger.top + trigger.height / 2 < box.top + box.height / 2;
        return `${side}-${expandDown ? "start" : "end"}`;
      }
    }
    const align = this._content.dataset.align || "center";
    if (align === "start") return `${side}-start`;
    if (align === "end") return `${side}-end`;
    return side;
  }

  // Scrollable ancestors of the trigger, nearest first. Used both to pick the
  // expand direction and to know which containers' scrolling should dismiss us.
  scrollableAncestors() {
    const result = [];
    let el = this.element.parentElement;
    while (el && el !== document.body) {
      if (/(auto|scroll)/.test(getComputedStyle(el).overflowY)) result.push(el);
      el = el.parentElement;
    }
    return result;
  }

  openPopover() {
    this.raiseAboveDialog();
    this.ensurePopper();
    // Reveal first so the list can be measured, cap it to the viewport, then let Popper position the
    // already-correctly-sized popover.
    this.show();
    this.sizeScrollerToViewport();
    this.popperInstance.setOptions({ placement: this.placementForOpen() });
    this.popperInstance.update();
    this.correctForTransformedAncestor();
  }

  // Never reparent: moving the content out strands sibling Stimulus targets (multiselect's `option`
  // targets live in here, `display` and `hiddenContainer` don't), silently breaking selection.
  raiseAboveDialog() {
    if (this._offsetApplied || !this.transformedAncestor()) return;

    this._offsetApplied = true;
    this._content.style.zIndex = ABOVE_DIALOG_Z_INDEX;
  }

  // A transformed ancestor is the containing block for `position: fixed`, so Popper's viewport
  // coordinates resolve against it -- subtract its offset.
  correctForTransformedAncestor() {
    if (!this._offsetApplied) return;

    const host = this.transformedAncestor();
    if (!host) return;

    const box = host.getBoundingClientRect();
    this._content.style.marginLeft = `${-box.left}px`;
    this._content.style.marginTop = `${-box.top}px`;
  }

  restoreContentPosition() {
    if (!this._offsetApplied) return;

    this._content.style.zIndex = "";
    this._content.style.marginLeft = "";
    this._content.style.marginTop = "";
    this._offsetApplied = false;
  }

  transformedAncestor() {
    let el = this.element.parentElement;
    while (el && el !== document.body) {
      const { transform, perspective, filter } = getComputedStyle(el);
      if (
        (transform && transform !== "none") ||
        (perspective && perspective !== "none") ||
        (filter && filter !== "none")
      ) {
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }

  updatePosition() {
    if (this.popperInstance) {
      this.popperInstance.update();
      // Popper recomputes viewport coordinates, so the dialog offset has to go back on. The margins are
      // absolute rather than cumulative, so re-applying is idempotent.
      this.correctForTransformedAncestor();
    }
  }

  disconnect() {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
    document.removeEventListener("click", this.clickOutsideListener);
    this.removeDismissListeners();
    if (this._enclosingDialog && this.dialogClosedListener) {
      this._enclosingDialog.removeEventListener(
        "ui--dialog:closed",
        this.dialogClosedListener,
      );
      this._enclosingDialog = null;
    }
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
    this.restoreContentPosition();
    this._content = null;
  }

  // Show the popover
  show() {
    // Elevate the closest sticky cell so the dropdown appears above other rows.
    this._elevatedCell = this.element.closest(".sticky");
    if (this._elevatedCell) {
      this._elevatedCell.style.zIndex = "50";
    }

    this._content.classList.remove("hidden");
    this._content.dataset.state = "open";
    // Add click-outside listener when the popover opens.
    setTimeout(() => {
      document.addEventListener("click", this.clickOutsideListener);
    }, 0);
    // The content is position:fixed, so it doesn't follow its trigger when an
    // ancestor scrolls — it would just float in place. Dismiss on scroll of the
    // trigger's scroll containers (the table card, the page) so scrollbar drags,
    // wheeling, and keyboard scrolling all close it.
    this._scrollAncestors = this.scrollableAncestors();
    this._scrollAncestors.forEach((el) =>
      el.addEventListener("scroll", this.dismissOnScrollListener, {
        passive: true,
      }),
    );
  }

  // Hide the popover
  hide() {
    clearTimeout(this._hoverTimeout);
    if (this._elevatedCell) {
      this._elevatedCell.style.zIndex = "";
      this._elevatedCell = null;
    }

    this._content.classList.add("hidden");
    this._content.dataset.state = "closed";
    document.removeEventListener("click", this.clickOutsideListener);
    this.removeDismissListeners();

    // Clear any filter inputs so the next open shows all options
    const filterInput = this._content.querySelector(
      '[data-ui--filter-target="source"]',
    );
    if (filterInput && filterInput.value) {
      filterInput.value = "";
      filterInput.dispatchEvent(new Event("input", { bubbles: true }));
    }

    this.restoreContentPosition();
  }

  removeDismissListeners() {
    (this._scrollAncestors || []).forEach((el) =>
      el.removeEventListener("scroll", this.dismissOnScrollListener),
    );
    this._scrollAncestors = null;
  }

  dismissOnScroll(event) {
    // Ignore scrolling inside the menu itself so a genuinely scrollable popover
    // isn't dismissed by its own scroll.
    if (!this._content.contains(event.target)) this.hide();
  }

  clickOutside(event) {
    if (
      !this.element.contains(event.target) &&
      !this._content.contains(event.target)
    ) {
      this.hide();
    }
  }

  // Allow type-to-search directly on the trigger: typing a printable
  // character opens the popover and seeds the filter input so users
  // don't have to click the dropdown first then click the search field.
  handleTriggerKeydown(event) {
    if (event.key === "Escape") {
      // Only consume the Escape when it actually closes an open popover, so an
      // enclosing dialog (whose close listener lives on window) doesn't close too.
      if (!this._content.classList.contains("hidden")) {
        event.stopPropagation();
        this.hide();
      }
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.openAndFocusFilter();
      return;
    }

    if (
      event.key.length !== 1 ||
      event.key === " " ||
      event.ctrlKey ||
      event.metaKey
    )
      return;

    event.preventDefault();
    this.openAndFocusFilter(event.key);
  }

  openAndFocusFilter(initialChar = null) {
    if (this._content.classList.contains("hidden")) {
      this.openPopover();
    }

    requestAnimationFrame(() => {
      const filterInput = this._content.querySelector(
        '[data-ui--filter-target="source"]',
      );
      if (filterInput) {
        if (initialChar) {
          filterInput.value = initialChar;
        }
        filterInput.focus();
        filterInput.dispatchEvent(new Event("input", { bubbles: true }));
      }
    });
  }

  // Show the popover on hover (with delay for smooth UX)
  showWithDelay() {
    if (this._hideTimeout) {
      clearTimeout(this._hideTimeout);
      this._hideTimeout = null;
    }
    if (this._content.classList.contains("hidden")) {
      this.openPopover();
    }
  }

  // Hide the popover on mouse leave (with delay so user can move to content)
  hideWithDelay() {
    this._hideTimeout = setTimeout(() => {
      this.hide();
      this._hideTimeout = null;
    }, 150);
  }

  // Toggle the popover on demand
  toggle(event) {
    if (this._content.classList.contains("hidden")) {
      this.openPopover();

      if (this.hasDismissAfterValue) {
        setTimeout(() => {
          this.hide();
        }, this.dismissAfterValue);
      }
    } else {
      this.hide();
    }
  }
}
